import { useCallback, useEffect, useMemo, useState } from "react";
import "maplibre-gl/dist/maplibre-gl.css";
import { Dialog } from "@base-ui/react/dialog";
import { DrawerPreview as Drawer } from "@base-ui/react/drawer";
import { IconCheck, IconChevronDown, IconCode, IconCurrentLocation, IconExclamationCircle, IconEye, IconInfoCircle, IconLoader2, IconMapPin, IconShare, IconSun } from "@tabler/icons-react";
import SunCalc from "suncalc";
import { OpeningHoursAccordion } from "./components/OpeningHoursAccordion";
import { MapView, type SearchTarget, type SelectedCafe } from "./components/MapView";
import { SearchBar, type SearchResult } from "./components/SearchBar";
import { WelcomeDialog } from "./components/WelcomeDialog";
import { TimeSlider } from "./components/TimeSlider";
import type { CafePointFeature, FetchStatus, ShadowTimelineEntry } from "./lib/types";
import { ALL_CATEGORIES, ALL_SUN, CategoryFilter, type CategoryValue, type SunValue } from "./components/CategoryFilter";
import { PlaceSuggestions } from "./components/PlaceSuggestions";
import { SunTimeline } from "./components/SunTimeline";
import { EmbedCodeDialog } from "./components/EmbedCodeDialog";

type StatusState = {
  status: FetchStatus;
  message: string;
};

const MINUTES_IN_DAY = 24 * 60;
const TIME_STEP_MINUTES = 15;
const FALLBACK_SUN_POSITION = { lat: 52.52, lng: 13.405 };

function clampMinutesOfDay(value: number) {
  return Math.max(0, Math.min(MINUTES_IN_DAY, value));
}

function snapToTimeStep(value: number) {
  return Math.round(value / TIME_STEP_MINUTES) * TIME_STEP_MINUTES;
}

function getGoogleMapsCafeUrl(cafe: SelectedCafe): string {
  const query = [cafe.name, cafe.address].filter(Boolean).join(" ").trim() || cafe.name;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function getGoogleStreetViewUrl(cafe: SelectedCafe): string {
  const [lng, lat] = cafe.coordinates;
  return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
}

function categoryLabel(category: string): string {
  if (category === "bar") return "Bar";
  if (category === "restaurant") return "Restaurant";
  return "Cafe";
}

export function App() {
  const [sunPosition, setSunPosition] = useState(FALLBACK_SUN_POSITION);
  const [minutesOfDay, setMinutesOfDay] = useState(() => {
    const now = new Date();
    return clampMinutesOfDay(snapToTimeStep(now.getHours() * 60 + now.getMinutes()));
  });
  const [recenterRequestToken, setRecenterRequestToken] = useState(0);
  const [isRecentering, setIsRecentering] = useState(false);
  const [searchTarget, setSearchTarget] = useState<SearchTarget | null>(null);
  const [selectedCafe, setSelectedCafe] = useState<SelectedCafe | null>(null);
  const [flyToCafeCoords, setFlyToCafeCoords] = useState<[number, number] | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<Set<CategoryValue>>(() => new Set(ALL_CATEGORIES));
  const [selectedSun, setSelectedSun] = useState<Set<SunValue>>(() => new Set(ALL_SUN));
  const [openOnly, setOpenOnly] = useState(false);
  const handleToggleCategory = useCallback((value: CategoryValue) => {
    setSelectedCategories(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);
  const handleToggleSun = useCallback((value: SunValue) => {
    setSelectedSun(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);
  const handleToggleOpen = useCallback(() => {
    setOpenOnly(prev => !prev);
  }, []);
  const [visiblePlaces, setVisiblePlaces] = useState<CafePointFeature[]>([]);
  const [shadowTimeline, setShadowTimeline] = useState<ShadowTimelineEntry[] | null>(null);
  const [statusState, setStatusState] = useState<StatusState>({
    status: "idle",
    message: "Move the map to load cafes",
  });

  const simulatedDate = useMemo(() => {
    const now = new Date();
    const nextDate = new Date(now);
    nextDate.setHours(0, 0, 0, 0);
    nextDate.setMinutes(minutesOfDay);
    return nextDate;
  }, [minutesOfDay]);
  const sunWindow = useMemo(() => {
    const dayDate = new Date(simulatedDate);
    dayDate.setHours(0, 0, 0, 0);
    const { sunrise, sunset } = SunCalc.getTimes(dayDate, sunPosition.lat, sunPosition.lng);
    return {
      sunriseMinutes: clampMinutesOfDay(sunrise.getHours() * 60 + sunrise.getMinutes()),
      sunsetMinutes: clampMinutesOfDay(sunset.getHours() * 60 + sunset.getMinutes()),
    };
  }, [simulatedDate, sunPosition]);
  useEffect(() => {
    if (!navigator.geolocation) {
      return;
    }
    navigator.geolocation.getCurrentPosition(
      position => {
        setSunPosition({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      () => {
        // Keep fallback city sun profile when geolocation is unavailable.
      },
      { enableHighAccuracy: false, maximumAge: 600_000, timeout: 2_000 },
    );
  }, []);

  const handleFetchStatusChange = useCallback((status: FetchStatus, message: string) => {
    setStatusState({ status, message });
  }, []);
  const handlePlacesReady = useCallback((features: CafePointFeature[]) => {
    setVisiblePlaces(features);
  }, []);
  const handleCafeSelect = useCallback((cafe: SelectedCafe | null) => {
    setSelectedCafe(cafe);
    setFlyToCafeCoords(cafe ? [...cafe.coordinates] as [number, number] : null);
    if (!cafe) setShadowTimeline(null);
  }, []);
  const handleShadowTimeline = useCallback((timeline: ShadowTimelineEntry[] | null) => {
    setShadowTimeline(timeline);
  }, []);
  const handleRecenterRequestComplete = useCallback(() => {
    setIsRecentering(false);
  }, []);
  const handleSearchSelect = useCallback((result: SearchResult) => {
    setSearchTarget({ center: result.center, zoom: result.zoom });
  }, []);
  const handleSearchComplete = useCallback(() => {
    setSearchTarget(null);
  }, []);
  const handleTimeChange = useCallback((nextMinutesOfDay: number) => {
    setMinutesOfDay(clampMinutesOfDay(snapToTimeStep(nextMinutesOfDay)));
  }, []);
  const [showMobileList, setShowMobileList] = useState(false);
  const [shareConfirmed, setShareConfirmed] = useState(false);
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  useEffect(() => {
    setEmbedDialogOpen(false);
  }, [selectedCafe]);
  const handleShare = useCallback(async () => {
    if (!selectedCafe) return;
    const mapsUrl = getGoogleMapsCafeUrl(selectedCafe);
    const label = categoryLabel(selectedCafe.category);
    const text = [selectedCafe.name, selectedCafe.address].filter(Boolean).join(" – ");
    const shareData = {
      title: `${selectedCafe.name} – Sunny.coffee`,
      text: `${text} (${label})\n\nFound on Sunny.coffee`,
      url: mapsUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // user cancelled share dialog
      }
    } else {
      await navigator.clipboard.writeText(`${shareData.text}\n${mapsUrl}`);
      setShareConfirmed(true);
      setTimeout(() => setShareConfirmed(false), 2000);
    }
  }, [selectedCafe]);

  const isLoading = statusState.status === "loading";
  const hasErrorStatus =
    statusState.status === "error" || /failed|error/i.test(statusState.message);

  return (
    <div className="relative h-dvh w-dvw overflow-hidden bg-slate-900">
      <WelcomeDialog />
      <MapView
        simulatedDate={simulatedDate}
        recenterRequestToken={recenterRequestToken}
        searchTarget={searchTarget}
        selectedCafeCoordinates={flyToCafeCoords}
        sunriseMinutes={sunWindow.sunriseMinutes}
        sunsetMinutes={sunWindow.sunsetMinutes}
        selectedCategories={selectedCategories}
        selectedSun={selectedSun}
        openOnly={openOnly}
        onPlacesReady={handlePlacesReady}
        onFetchStatusChange={handleFetchStatusChange}
        onCafeSelect={handleCafeSelect}
        onShadowTimeline={handleShadowTimeline}
        onRecenterRequestComplete={handleRecenterRequestComplete}
        onSearchComplete={handleSearchComplete}
      />

      <div className="pointer-events-none absolute left-2 top-2 z-10 flex flex-col gap-2 sm:left-4 sm:top-4 sm:flex-row sm:items-start sm:gap-3">
      <aside className="pointer-events-auto relative z-20 w-[min(26rem,calc(100vw-1rem))] rounded-2xl border border-slate-200/80 bg-white/95 p-3 shadow-xl backdrop-blur sm:w-[min(26rem,calc(100vw-2rem))] sm:p-4">
        <div className="flex items-center justify-between gap-3">
          <h1 className="inline-flex items-center gap-2 text-lg font-semibold text-slate-900">
            {isLoading ? (
              <IconLoader2 size={24} stroke={2.5} className="animate-spin text-amber-500" style={{ animationDuration: "1s" }} />
            ) : (
              <IconSun size={24} stroke={2.5} className="text-amber-500" />
            )}
            <span>Sunny.coffee</span>
          </h1>
          <Dialog.Root>
            <Dialog.Trigger
              aria-label="Open attribution info"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:bg-slate-100"
            >
              {hasErrorStatus ? (
                <IconExclamationCircle size={18} stroke={2} className="text-red-600" />
              ) : (
                <IconInfoCircle size={18} stroke={2} />
              )}
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Backdrop className="fixed inset-0 z-30 bg-slate-900/40" />
              <Dialog.Viewport className="fixed inset-0 z-40 flex items-center justify-center p-4">
                <Dialog.Popup className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl">
                  <div className="flex items-center justify-between gap-3">
                    <Dialog.Title className="text-base font-semibold text-slate-900">Attribution</Dialog.Title>
                    <Dialog.Close
                      aria-label="Close attribution info"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:bg-slate-100"
                    >
                      ×
                    </Dialog.Close>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-slate-700">
                    <a className="underline" href="https://maplibre.org/" target="_blank" rel="noreferrer">
                      MapLibre
                    </a>{" "}
                    |{" "}
                    <a className="underline" href="https://openfreemap.org/" target="_blank" rel="noreferrer">
                      OpenFreeMap
                    </a>{" "}
                    | ©{" "}
                    <a className="underline" href="https://www.openmaptiles.org/" target="_blank" rel="noreferrer">
                      OpenMapTiles
                    </a>{" "}
                    Data from{" "}
                    <a className="underline" href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">
                      OpenStreetMap
                    </a>
                  </p>
                  <h3 className="mt-6 text-sm font-semibold text-slate-700">Status</h3>
                  <p className="mt-1 text-sm text-slate-700">{statusState.message}</p>
                </Dialog.Popup>
              </Dialog.Viewport>
            </Dialog.Portal>
          </Dialog.Root>
        </div>
        <p className="mt-1 hidden text-sm text-slate-600 sm:block">Find sunny spots for coffee, drinks & more.</p>
        <SearchBar onSelect={handleSearchSelect} />
        <button
          type="button"
          onClick={() => setShowMobileList(prev => !prev)}
          className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg border border-slate-200 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-slate-50 sm:hidden"
        >
          {showMobileList ? "Hide option" : "Show option"}
          <IconChevronDown size={14} stroke={2} className={`transition-transform ${showMobileList ? "rotate-180" : ""}`} />
        </button>
        <div className={showMobileList ? "" : "hidden sm:block"}>
          <PlaceSuggestions
            places={visiblePlaces}
            selectedCategories={selectedCategories}
            selectedSun={selectedSun}
            openOnly={openOnly}
            simulatedDate={simulatedDate}
            onPlaceClick={handleCafeSelect}
          />
        </div>
      </aside>
        <div className="pointer-events-auto relative z-10 w-fit max-w-[min(26rem,calc(100vw-1rem))] sm:max-w-[calc(100vw-30rem)]">
          <CategoryFilter
            selectedCategories={selectedCategories}
            selectedSun={selectedSun}
            openOnly={openOnly}
            onToggleCategory={handleToggleCategory}
            onToggleSun={handleToggleSun}
            onToggleOpen={handleToggleOpen}
          />
        </div>
      </div>

      <aside className="absolute bottom-0 left-1/2 z-10 w-[min(26rem,calc(100vw-2rem))] -translate-x-1/2">
        <TimeSlider
          minutesOfDay={minutesOfDay}
          sunriseMinutes={sunWindow.sunriseMinutes}
          sunsetMinutes={sunWindow.sunsetMinutes}
          onChange={handleTimeChange}
        />
      </aside>

      <button
        type="button"
        aria-label="Go to current location"
        disabled={isRecentering}
        onClick={() => {
          setIsRecentering(true);
          setRecenterRequestToken(previous => previous + 1);
        }}
        className="absolute bottom-22 right-4 z-20 inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200/80 bg-white/95 text-slate-600 shadow-lg backdrop-blur transition hover:bg-white disabled:cursor-wait disabled:opacity-80"
      >
        <IconCurrentLocation size={20} stroke={2.25} />
      </button>

      {isRecentering ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-slate-900/20 backdrop-blur-[1px]"
        >
          <IconLoader2 size={56} stroke={2.25} className="animate-spin text-white" style={{ animationDuration: "1s" }} />
        </div>
      ) : null}

      <Drawer.Root
        open={selectedCafe !== null}
        modal={false}
        onOpenChange={(open: boolean) => {
          if (!open && embedDialogOpen) return;
          if (!open) setSelectedCafe(null);
        }}
      >
        <Drawer.Portal>
          <Drawer.Viewport className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex items-end justify-center">
            <Drawer.Popup className="pointer-events-auto w-full max-w-md rounded-t-2xl border border-slate-200 bg-white p-3 shadow-2xl sm:p-4">
              <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-slate-300" />
              <div className="flex items-center justify-between gap-3">
                <Drawer.Title className="text-base font-semibold text-slate-900">
                  {selectedCafe?.name ?? (selectedCafe ? categoryLabel(selectedCafe.category) : "Cafe")}
                </Drawer.Title>
              </div>
              {selectedCafe?.address ? (
                <p className="mt-1 text-sm text-slate-500">{selectedCafe.address}</p>
              ) : null}
              {selectedCafe?.outdoorSeating?.trim() &&
              selectedCafe.outdoorSeating.trim().toLowerCase() !== "unknown" ? (
                <p className="mt-3 text-sm text-slate-700">Outdoor seating: {selectedCafe.outdoorSeating}</p>
              ) : null}

              {shadowTimeline ? (
                <SunTimeline
                  timeline={shadowTimeline}
                  currentMinutes={minutesOfDay}
                  sunriseMinutes={sunWindow.sunriseMinutes}
                  sunsetMinutes={sunWindow.sunsetMinutes}
                />
              ) : null}

              <OpeningHoursAccordion cafe={selectedCafe} simulatedDate={simulatedDate} />

              {selectedCafe ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  <a
                    href={getGoogleMapsCafeUrl(selectedCafe)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                  >
                    <IconMapPin size={16} stroke={2} />
                    Google Maps
                  </a>
                  <a
                    href={getGoogleStreetViewUrl(selectedCafe)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                  >
                    <IconEye size={16} stroke={2} />
                    Street View
                  </a>
                  <button
                    type="button"
                    onClick={() => void handleShare()}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                  >
                    {shareConfirmed ? <IconCheck size={16} stroke={2} className="text-emerald-500" /> : <IconShare size={16} stroke={2} />}
                    {shareConfirmed ? "Copied!" : "Share"}
                  </button>
                  {/* <button
                    type="button"
                    onClick={() => setEmbedDialogOpen(true)}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                  >
                    <IconCode size={16} stroke={2} />
                    Embed
                  </button> */}
                </div>
              ) : null}
            </Drawer.Popup>
          </Drawer.Viewport>
        </Drawer.Portal>
      </Drawer.Root>

      {selectedCafe ? (
        <EmbedCodeDialog
          open={embedDialogOpen}
          onOpenChange={setEmbedDialogOpen}
          coordinates={selectedCafe.coordinates}
          defaultName={selectedCafe.name}
        />
      ) : null}
    </div>
  );
}

export default App;
